/**
 * stripHtml (client mirror) — turn HTML-laced description/summary fields
 * (e.g. imported from ServiceTitan) into plain text for display.
 *
 * Mirrors the server util at src/server/utils/htmlText.ts. Some stored
 * fields contain simple rich-text tags (<span>, <b>, <br>, <div>, <p>, …)
 * and HTML entities. We render fields as plain React children, so React
 * escapes the angle brackets and the user sees literal "<div><br></div>".
 * Strip on the way to the screen so the value reads as text.
 *
 * Intentionally regex-based and dependency-free. This is NOT an XSS
 * sanitizer — never feed the result to dangerouslySetInnerHTML.
 */

const ENTITIES: Record<string, string> = {
    "&amp;": "&",
    "&lt;": "<",
    "&gt;": ">",
    "&quot;": '"',
    "&apos;": "'",
    "&#39;": "'",
    "&nbsp;": " ",
};

function decodeEntities(s: string): string {
    return s
        .replace(/&[a-zA-Z]+;|&#\d+;/g, (m) => ENTITIES[m] ?? m)
        .replace(/&#(\d+);/g, (_, n) => String.fromCharCode(Number(n)));
}

// Restrict tag stripping to the rich-text vocabulary we actually see so
// that strings like "Tuan Ngo <tngo@nrhtx.com>" survive intact.
const KNOWN_TAGS =
    "(?:span|b|strong|i|em|u|s|font|small|sub|sup|a|img|table|tbody|thead|tfoot|ul|ol|center|pre|code|blockquote)";
const KNOWN_BLOCK_TAGS = "(?:div|p|li|tr|td|th|h[1-6])";

export function stripHtml(input: string | null | undefined): string {
    if (!input) return "";
    let out = input;
    out = out.replace(/<\s*(br|hr)\s*\/?\s*>/gi, " ");
    out = out.replace(new RegExp(`<\\/?\\s*${KNOWN_BLOCK_TAGS}\\b[^>]*>`, "gi"), " ");
    out = out.replace(new RegExp(`<\\/?\\s*${KNOWN_TAGS}\\b[^>]*>`, "gi"), "");
    out = decodeEntities(out);
    out = out.replace(/\s+/g, " ").trim();
    return out;
}
